import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCurrentWorkspaceService } from '@/services/workspaces';
import { UserPlus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';

type AssigneesBadgeProps = {
  assignedMemberIds: string[];
  onAssign: (
    memberIds: string[],
    callbacks?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
};

export const AssigneesBadge: FC<AssigneesBadgeProps> = ({
  assignedMemberIds,
  onAssign,
}) => {
  const { currentWorkspace } = useCurrentWorkspaceService();
  const [optimistic, setOptimistic] = useState<string[] | null>(null);
  const [open, setOpen] = useState(false);

  const members = currentWorkspace?.members ?? [];
  const effective = optimistic ?? assignedMemberIds;

  // Clear optimistic state once server data catches up.
  useEffect(() => {
    if (
      optimistic &&
      optimistic.length === assignedMemberIds.length &&
      optimistic.every(id => assignedMemberIds.includes(id))
    ) {
      setOptimistic(null);
    }
  }, [assignedMemberIds, optimistic]);

  const memberOptions: FilterOption[] = useMemo(
    () =>
      members.map(m => ({
        label: `${m.firstName} ${m.lastName}`.trim() || m.email,
        value: m.id,
      })),
    [members]
  );

  const handleChange = useCallback(
    (selected: string[]) => {
      setOptimistic(selected);
      onAssign(selected);
    },
    [onAssign]
  );

  const assignedMembers = members.filter(m => effective.includes(m.id));

  const initials = (m: { firstName: string; lastName: string }) =>
    `${m.firstName.charAt(0)}${m.lastName.charAt(0)}`.toUpperCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {assignedMembers.length === 0 ? (
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-neutral-300 px-2.5 py-1 text-xs text-neutral-500 hover:bg-neutral-50 transition-colors">
            <UserPlus className="h-3.5 w-3.5" />
            Assign
          </button>
        ) : (
          <button className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1 hover:bg-neutral-100 transition-colors">
            <div className="flex -space-x-1.5">
              {assignedMembers.slice(0, 3).map(m => (
                <div
                  key={m.id}
                  className="h-5 w-5 rounded-full bg-neutral-800 text-white flex items-center justify-center text-[9px] font-medium ring-1 ring-white"
                  title={`${m.firstName} ${m.lastName}`}
                >
                  {initials(m)}
                </div>
              ))}
            </div>
            {assignedMembers.length > 3 && (
              <span className="text-[10px] text-neutral-500 ml-0.5">
                +{assignedMembers.length - 3}
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[200px] p-0"
        align="start"
        sideOffset={4}
      >
        <FilterOptionList
          options={memberOptions}
          selected={effective}
          onChange={handleChange}
        />
      </PopoverContent>
    </Popover>
  );
};
